import React, { useImperativeHandle } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useController, FieldError } from 'react-hook-form';
import styled from 'styled-components';
import { ErrorMessage, useScreenQuery } from 'playup-web-core';

import BirthDateInput from '../../components/BirthDateInput/BirthDateInput';
import { Title } from './ControlledAddressInput';
import { isSafari } from '../../utils/isSafari';

dayjs.extend(customParseFormat);

const ControlledBirthDateInput = ({ disabled }: ControlledBirthDateInputProps) => {
	const {
		field,
		fieldState: { error },
	} = useController({
		name: 'birth_date',
	});
	const { isSmall } = useScreenQuery();
	const inputRef = React.useRef<HTMLInputElement>(null);
	useImperativeHandle(
		field.ref,
		() => ({
			focus: () => {
				if (!isSmall) {
					inputRef.current?.focus();
					//@ts-ignore
					inputRef.current?.showPicker?.();
				} else {
					inputRef.current?.focus();
				}
			},
		}),
		[isSmall]
	);
	return (
		<Container>
			<Title isSmall={isSmall}>Date of Birth</Title>
			<InputWrapper>
				{isSafari() && !isSmall ? (
					<BirthDateInputFormatter ref={inputRef} error={error} isSmallScreen={isSmall} onChange={field.onChange} />
				) : (
					<Input
						error={error}
						type={'date'}
						max={dayjs().subtract(18, 'year').format('YYYY-MM-DD')}
						min="1900-01-01"
						placeholder="Must match your ID"
						disabled={disabled}
						isSmallScreen={isSmall}
						value={field.value}
						onChange={field.onChange}
						onBlur={field.onBlur}
						ref={inputRef}
					/>
				)}

				{error && <ErrorMessage textAlign="left">{error.message}</ErrorMessage>}
			</InputWrapper>
		</Container>
	);
};

export default ControlledBirthDateInput;
interface ControlledBirthDateInputProps {
	disabled: boolean;
}
export const Input = styled.input<{
	isSmallScreen?: boolean;
	buttonPadding?: React.CSSProperties['padding'];
	error?: FieldError | undefined;
}>`
	display: flex;
	align-items: center;
	position: relative;
	width: ${({ isSmallScreen }) =>
		isSmallScreen ? (/^((?!chrome|android).)*safari/i.test(navigator.userAgent) ? '90vw' : '100%') : '100%'};
	height: ${({ isSmallScreen }) => (isSmallScreen ? '40px' : '50px')};
	padding: ${({ isSmallScreen }) => (isSmallScreen ? '10px' : '10px 18px')};
	padding-right: calc(${({ buttonPadding }) => buttonPadding} + 0.5rem);
	border-radius: 4px;
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '14px' : '18px')};
	color: #2d3134;
	background-color: #fff;
	font-family: Barlow, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
		'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
	text-align: left;
	-webkit-appearance: textfield;
	-moz-appearance: textfield;
	&:focus {
		outline: none;
	}

	::placeholder {
		color: #a4a6a8;
	}
	border: 2px solid ${({ theme, error }) => (error ? theme.colors.danger : '#DDE1E3')};
`;
const BirthDateInputFormatter = React.forwardRef(
	({ error, isSmallScreen, onChange }: BirthDateInputFormatterProps, ref) => {
		const [value, setValue] = React.useState('');

		React.useEffect(() => {
			const date = dayjs(value, 'DD/MM/YYYY');
			if (date.isValid()) {
				onChange(date.format('YYYY-MM-DD'));
			}
		}, [onChange, value]);

		return (
			<BirthDateInput
				ref={ref}
				formError={error}
				value={value}
				isSmallScreen={isSmallScreen}
				onChange={(value) => {
					setValue(value);
				}}
			/>
		);
	}
);
interface BirthDateInputFormatterProps {
	error: FieldError | undefined;
	isSmallScreen: boolean;
	onChange: (date: any) => void;
}

const Container = styled.div({
	flexDirection: 'column',
	flex: 1,
});

const InputWrapper = styled.div(({ theme }) => ({
	display: 'flex',
	marginTop: 7,
	marginBottom: 20,
	flexDirection: 'column',
}));
